import { useMemo, useRef, useState, type FormEvent } from "react";
import { formatStoredPath } from "../domain/paths";
import { planWorkspaceMove } from "../workspace/workspace_move";
import type { ScannedFolder } from "../workspace/workspace_scan";
import { Modal } from "./modal";

export interface FolderMoveDialogProps {
  readonly folder: ScannedFolder;
  readonly folders: readonly ScannedFolder[];
  readonly onClose: () => void;
  readonly onMove: (folder: ScannedFolder, destinationPath: readonly string[]) => Promise<void>;
}

/** Provides the keyboard-accessible equivalent of dragging a folder in the navigator. */
export function FolderMoveDialog({ folder, folders, onClose, onMove }: FolderMoveDialogProps) {
  const destinations = useMemo(
    () =>
      [
        { label: "Workspace", path: [] as readonly string[] },
        ...folders.map((candidate) => ({
          label: formatStoredPath(candidate.path),
          path: candidate.path,
        })),
      ].filter(({ path }) => {
        try {
          planWorkspaceMove(folder, path);
          return true;
        } catch {
          return false;
        }
      }),
    [folder, folders],
  );
  const [destination, setDestination] = useState(
    destinations[0] === undefined ? "" : formatStoredPath(destinations[0].path),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  /** Runs the same journaled move as pointer drag-and-drop. */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const selected = destinations.find(({ path }) => formatStoredPath(path) === destination);
    if (selected === undefined) {
      setError("Choose an available destination folder.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onMove(folder, selected.path);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The folder could not be moved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Move ${folder.name}`}
      titleId="move-folder-title"
      initialFocusRef={selectRef}
      onClose={onClose}
    >
      <form className="management-dialog" onSubmit={(event) => void handleSubmit(event)}>
        <p>Shotboard will copy, verify, then remove the original folder.</p>
        {destinations.length === 0 ? (
          <p className="form-error" role="status">
            Create another folder before moving this one.
          </p>
        ) : (
          <label>
            Destination folder
            <select
              ref={selectRef}
              value={destination}
              onChange={(event) => setDestination(event.currentTarget.value)}
            >
              {destinations.map(({ label, path }) => (
                <option key={formatStoredPath(path)} value={formatStoredPath(path)}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <footer className="modal__actions">
          <button type="button" className="secondary-action" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary-action"
            disabled={busy || destinations.length === 0}
          >
            Move Folder
          </button>
        </footer>
      </form>
    </Modal>
  );
}
